import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { User, Briefcase, ChevronDown, AlertTriangle } from 'lucide-react';

import { getApprovalFlow } from '../../../services/approvalFlowService';
import Avatar from '../../../components/ui/Avatar';

const EmptyText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  text-align: center;
  padding: 24px;
`;

const LoadingText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  text-align: center;
  padding: 24px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  margin-bottom: 20px;
`;

const StepCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  width: 100%;
  max-width: 600px;
`;

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary.main};
  color: #fff;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
`;

const TypeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${({ theme }) => `${theme.colors.primary.main}10`};
  color: ${({ theme }) => theme.colors.primary.main};
  flex-shrink: 0;
`;

const StepInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const StepName = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StepMeta = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ theme }) => `${theme.colors.primary.main}15`};
  color: ${({ theme }) => theme.colors.primary.main};
  flex-shrink: 0;
`;

const VacantBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: #FFF3E0;
  color: #E65100;
  flex-shrink: 0;
`;

const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  color: #A0A8B4;
`;

export default function FlowStepsTab({ flowId }) {
  const { t } = useTranslation();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!flowId) return;
    setLoading(true);
    getApprovalFlow(flowId)
      .then((res) => {
        const flow = res.data?.data;
        setSteps(flow?.steps || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [flowId]);

  if (loading) {
    return <LoadingText>{t('common.loading')}</LoadingText>;
  }

  if (steps.length === 0) {
    return <EmptyText>{t('approvalFlow.noSteps')}</EmptyText>;
  }

  const sortedSteps = [...steps].sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));

  return (
    <List>
      {sortedSteps.map((step, index) => (
        <React.Fragment key={step.id || index}>
          {index > 0 && (
            <ArrowContainer>
              <ChevronDown size={20} />
            </ArrowContainer>
          )}
          <StepCard>
            <StepNumber>{step.stepOrder || index + 1}</StepNumber>

            <TypeIcon>
              {step.approverType === 'person' ? <User size={16} /> : <Briefcase size={16} />}
            </TypeIcon>

            {step.approverType === 'person' && step.approverProfilePicture && (
              <Avatar
                src={step.approverProfilePicture}
                firstName={(step.approverName || '').split(' ')[0] || ''}
                lastName={(step.approverName || '').split(' ').slice(1).join(' ') || ''}
                size={36}
              />
            )}

            <StepInfo>
              <StepName>{step.approverName || '-'}</StepName>
              {step.approverType === 'position' && (
                <StepMeta>
                  {[step.approverCode, step.approverDepartment, step.approverTitle].filter(Boolean).join(' - ')}
                </StepMeta>
              )}
            </StepInfo>

            <Badge>
              {step.approverType === 'person'
                ? t('approvalFlow.approverPerson')
                : t('approvalFlow.approverPosition')}
            </Badge>

            {step.isVacant && (
              <VacantBadge>
                <AlertTriangle size={12} />
                {t('approvalFlow.vacantPosition')}
              </VacantBadge>
            )}
          </StepCard>
        </React.Fragment>
      ))}
    </List>
  );
}
